/**
 * Component attributes - types, families, density level, etc
 * @module ComponentAttribute
 */
module.exports = {
    /**
     * @enum {string}
     */
    type: {
        AXIAL: {
            name: 'AXIAL',
            family: {
                "RESISTOR": 'Resistor',
                "NONPOLARIZED_CAPACITOR": 'Capacitor - Non polarized',
                'POLARIZED_CAPACITOR': 'Polarized Capacitor',
                "DIODE": 'Diode',
                "FUSE": 'Fuse',
                "INDUCTOR": 'Inductor'
            }
        },

        HEADER: {
            name: 'HEADER',
            /*
                Each sequence has a key and origin position id. Origin is defined by the location where pin 1 is located in footprint.
                Position id can take values from this set {0, 1, 2, 3}
                Where,
                    0: top-left corner,
                    1: top-right corner,
                    2: bottom-right corner,
                    3: bottom-left corner
             */
            pinNumberSequencePattern: {
                "LRCCW": {key: 'LRCCW', description: 'Counter-clockwise from bottom left', pinOnePositionId: 3}, //left->right, anti-clockwise
                "LRCW": {key: 'LRCW', description: 'Clockwise from top left', pinOnePositionId: 0}, //left->right, clockwise
                "ZZBT": {key: 'ZZBT', description: 'ZigZag from bottom left', pinOnePositionId: 3} //zigzag, bottom->top
                //can support following in future
                //'ZZTB', //zigzag, top->bottom
                //'LRBT', //left->right, bottom->top
                //'LRTB' //left->right, top->bottom
            },

            footprintOriginLocation: {
                "PIN_ONE" : "Pin 1",
                "CENTER_PADS": "Center of pads"
            }
        },

        RADIALROUND: {
            name: 'RADIALROUND',
            family: {
                "POLARIZED_CAPACITOR": 'Polarized Capacitor',
                "NONPOLARIZED_CAPACITOR": 'Capacitor - Non polarized',
                "POLARIZED_INDUCTOR": 'Polarized Inductor',
                "NONPOLARIZED_INDUCTOR": 'Inductor - Non polarized',
            }
        },

        ELECTRO_MECHANICAL: {
            types: {
                STANDOFF_SPACER: {
                    name: "Standoff/Spacer",
                    genders: {
                        FEMALE: "Female",
                        MALE: "Male",
                        MALE_FEMALE: "Male-Female"
                    },
                    bodyShapes: {
                        "ROUND": "Round",
                        "HEX": "Hex",
                        "SQUARE": "Square"
                    }
                },

                BOARD_SUPPORT: "Board Support"
            }
        }
    },

    /*
        @enum {string}
     */
    padShapes: {
        "Round": 'Round',
        "Square": 'Square',
        "RoundedSquare": 'Rounded Square'
    },

    leadShapes: {
        "Round": 'Round',
        "Square": 'Square',
        "Rectangle": 'Rectangle'
    },

    bodyColors: {
        RADIAL:{
            ROUND:{
                CAPACITOR:{
                    Black: {key:"Black", value:'#0A0A0A'},
                    Green: {key:"Green", value:'#1E5F41'},
                    Blue: {key:"Blue", value:'#1825F8'},
                    Yellow: {key:"Yellow", value:'#FFFF00'},
                    Amber: {key:"Amber", value:'#FA9641'},
                    Brown: {key:"Brown", value:'#503741'},
                    Cyan: {key:"Cyan", value:'#00FFFF'},
                    Maroon: {key:"Maroon", value:'#800000'}
                },
                INDUCTOR:{
                    Black: {key:"Black", value:'#0A0A0A'},
                    Brown: {key:"Brown", value:'#503741'},
                    Grey: {key:"Grey", value:'#808080'},
                    Silver: {key:"Silver", value:'#C0C0C0'}
                },
                LED:{
                    Green: {key:"Green", value:'#1E5F41'},
                    Blue: {key:"Blue", value:'#1825F8'},
                    Yellow: {key:"Yellow", value:'#FFFF00'},
                    White: {key:"White", value:'#FFFFFF'},
                    Red: {key:"Red", value:'#FF0000'},
                    Amber: {key:"Amber", value:'#FA9641'},
                    Purple: {key:"Purple", value:'#800080'},
                    Black: {key:"Black", value:'#0A0A0A'},
                    Custom: {key:"Custom", value: ''}

                }

            }
        }
    },

    /**
     * @enum {number}
     */
    densityLevel: {
        'Most (A)': 0,
        'Nominal (B)': 1,
        'Least (C)': 2
    },

    /**
     * Thread Sizes (supported in Fusion) for electro-mechanical parts
     */
    threadTypes: {
        ANSI: {
            name: 'ANSI',
            defaultSize: "3.0 mm",
            sizes: {
                "3.0 mm": {
                    "4-40": "4-40",
                    "7-70": "7-70"
                }
            }
        },
        ISO_METRIC: {
            name: "ISO Metric profile",
            defaultSize: "3.0 mm",
            sizes: {
                "0.25 mm": {
                    "M0.25x0.075": "M0.25x0.075"
                },
                "0.3 mm": {
                    "M0.3x0.09": "M0.3x0.09",
                    "M0.3x0.08": "M0.3x0.08"
                },
                "0.35 mm": {
                    "M0.35x0.09": "M0.35x0.09"
                },
                "0.4 mm": {
                    "M0.4x0.1": "M0.4x0.1"
                },
                "0.45 mm": {
                    "M0.45x0.1": "M0.45x0.1"
                },
                "0.5 mm": {
                    "M0.5x0.125": "M0.5x0.125"
                },
                "0.55 mm": {
                    "M0.55x0.125": "M0.55x0.125"
                },
                "0.6 mm": {
                    "M0.6x0.15": "M0.6x0.15"
                },
                "0.7 mm": {
                    "M0.7x0.175": "M0.7x0.175"
                },
                "0.8 mm": {
                    "M0.8x0.2": "M0.8x0.2"
                },
                "0.9 mm": {
                    "M0.9x0.225": "M0.9x0.225"
                },
                "1.0 mm": {
                    "M1x0.25": "M1x0.25",
                    "M1x0.2": "M1x0.2"
                },
                "1.1 mm": {
                    "M1.1x0.25": "M1.1x0.25",
                    "M1.1x0.2": "M1.1x0.2"
                },
                "1.2 mm": {
                    "M1.2x0.25": "M1.2x0.25",
                    "M1.2x0.2": "M1.2x0.2"
                },
                "1.4 mm": {
                    "M1.4x0.3": "M1.4x0.3",
                    "M1.4x0.2": "M1.4x0.2"
                },
                "1.6 mm": {
                    "M1.6x0.35": "M1.6x0.35",
                    "M1.6x0.3": "M1.6x0.3",
                    "M1.6x0.2": "M1.6x0.2"
                },
                "1.7 mm": {
                    "M1.7x0.35": "M1.7x0.35"
                },
                "1.8 mm": {
                    "M1.8x0.35": "M1.8x0.35",
                    "M1.8x0.2": "M1.8x0.2"
                },
                "2.0 mm": {
                    "M2x0.4": "M2x0.4",
                    "M2x0.25": "M2x0.25"
                },
                "2.2 mm": {
                    "M2.2x0.45": "M2.2x0.45",
                    "M2.2x0.25": "M2.2x0.25"
                },
                "2.3 mm": {
                    "M2.3x0.45": "M2.3x0.45",
                    "M2.3x0.4": "M2.3x0.4"
                },
                "2.5 mm": {
                    "M2.5x0.45": "M2.5x0.45",
                    "M2.5x0.35": "M2.5x0.35"
                },
                "2.6 mm": {
                    "M2.6x0.45": "M2.6x0.45"
                },
                "3.0 mm": {
                    "M3x0.5": "M3x0.5",
                    "M3x0.35": "M3x0.35"
                },
                "3.5 mm": {
                    "M3.5x0.6": "M3.5x0.6",
                    "M3.5x0.35": "M3.5x0.35"
                },
                "4.0 mm": {
                    "M4x0.7": "M4x0.7",
                    "M4x0.5": "M4x0.5"
                },
                "4.5 mm": {
                    "M4.5x0.75": "M4.5x0.75",
                    "M4.5x0.5": "M4.5x0.5"
                },
                "5.0 mm": {
                    "M5x0.8": "M5x0.8",
                    "M5x0.5": "M5x0.5"
                },
                "5.5 mm": {
                    "M5.5x0.5": "M5.5x0.5"
                },
                "6.0 mm": {
                    "M6x1": "M6x1",
                    "M6x0.8": "M6x0.8",
                    "M6x0.75": "M6x0.75",
                    "M6x0.7": "M6x0.7",
                    "M6x0.5": "M6x0.5"
                },
                "7.0 mm": {
                    "M7x1": "M7x1",
                    "M7x0.75": "M7x0.75",
                    "M7x0.5": "M7x0.5"
                },
                "8.0 mm": {
                    "M8x1.25": "M8x1.25",
                    "M8x1": "M8x1",
                    "M8x0.8": "M8x0.8",
                    "M8x0.75": "M8x0.75",
                    "M8x0.5": "M8x0.5"
                },
                "9.0 mm": {
                    "M9x1.25": "M9x1.25",
                    "M9x1": "M9x1",
                    "M9x0.75": "M9x0.75",
                    "M9x0.5": "M9x0.5"
                },
                "10.0 mm": {
                    "M10x1.5": "M10x1.5",
                    "M10x1.25": "M10x1.25",
                    "M10x1.12": "M10x1.12",
                    "M10x1": "M10x1",
                    "M10x0.75": "M10x0.75",
                    "M10x0.5": "M10x0.5"
                },
                "11.0 mm": {
                    "M11x1.5": "M11x1.5",
                    "M11x1": "M11x1",
                    "M11x0.75": "M11x0.75",
                    "M11x0.5": "M11x0.5"
                },
                "12.0 mm": {
                    "M12x1.75": "M12x1.75",
                    "M12x1.5": "M12x1.5",
                    "M12x1.25": "M12x1.25",
                    "M12x1": "M12x1",
                    "M12x0.75": "M12x0.75",
                    "M12x0.5": "M12x0.5"
                },
                "14.0 mm": {
                    "M14x2": "M14x2",
                    "M14x1.5": "M14x1.5",
                    "M14x1.25": "M14x1.25",
                    "M14x1": "M14x1",
                    "M14x0.75": "M14x0.75",
                    "M14x0.5": "M14x0.5"
                },
                "15.0 mm": {
                    "M15x1.5": "M15x1.5",
                    "M15x1": "M15x1"
                },
                "16.0 mm": {
                    "M16x2": "M16x2",
                    "M16x1.6": "M16x1.6",
                    "M16x1.5": "M16x1.5",
                    "M16x1.25": "M16x1.25",
                    "M16x1": "M16x1",
                    "M16x0.75": "M16x0.75",
                    "M16x0.5": "M16x0.5"
                },
                "17.0 mm": {
                    "M17x1.5": "M17x1.5",
                    "M17x1": "M17x1"
                },
                "18.0 mm": {
                    "M18x2.5": "M18x2.5",
                    "M18x2": "M18x2",
                    "M18x1.5": "M18x1.5",
                    "M18x1.25": "M18x1.25",
                    "M18x1": "M18x1",
                    "M18x0.75": "M18x0.75",
                    "M18x0.5": "M18x0.5"
                },
                "20.0 mm": {
                    "M20x2.5": "M20x2.5",
                    "M20x2": "M20x2",
                    "M20x1.5": "M20x1.5",
                    "M20x1": "M20x1",
                    "M20x0.75": "M20x0.75",
                    "M20x0.5": "M20x0.5"
                },
                "22.0 mm": {
                    "M22x3": "M22x3",
                    "M22x2.5": "M22x2.5",
                    "M22x2": "M22x2",
                    "M22x1.5": "M22x1.5",
                    "M22x1": "M22x1",
                    "M22x0.75": "M22x0.75",
                    "M22x0.5": "M22x0.5"
                },
                "24.0 mm": {
                    "M24x3": "M24x3",
                    "M24x2.5": "M24x2.5",
                    "M24x2": "M24x2",
                    "M24x1.5": "M24x1.5",
                    "M24x1": "M24x1",
                    "M24x0.75": "M24x0.75"
                },
                "25.0 mm": {
                    "M25x2": "M25x2",
                    "M25x1.5": "M25x1.5",
                    "M25x1": "M25x1"
                },
                "26.0 mm": {
                    "M26x1.5": "M26x1.5"
                },
                "27.0 mm": {
                    "M27x3": "M27x3",
                    "M27x2": "M27x2",
                    "M27x1.5": "M27x1.5",
                    "M27x1": "M27x1",
                    "M27x0.75": "M27x0.75"
                },
                "28.0 mm": {
                    "M28x2": "M28x2",
                    "M28x1.5": "M28x1.5",
                    "M28x1": "M28x1"
                },
                "30.0 mm": {
                    "M30x3.5": "M30x3.5",
                    "M30x3": "M30x3",
                    "M30x2.5": "M30x2.5",
                    "M30x2": "M30x2",
                    "M30x1.5": "M30x1.5",
                    "M30x1": "M30x1",
                    "M30x0.75": "M30x0.75"
                },
                "32.0 mm": {
                    "M32x2": "M32x2",
                    "M32x1.5": "M32x1.5"
                },
                "33.0 mm": {
                    "M33x3.5": "M33x3.5",
                    "M33x3": "M33x3",
                    "M33x2": "M33x2",
                    "M33x1.5": "M33x1.5",
                    "M33x1": "M33x1",
                    "M33x0.75": "M33x0.75"
                },
                "35.0 mm": {
                    "M35x1.5": "M35x1.5"
                },
                "36.0 mm": {
                    "M36x4": "M36x4",
                    "M36x3": "M36x3",
                    "M36x2": "M36x2",
                    "M36x1.5": "M36x1.5",
                    "M36x1": "M36x1"
                },
                "38.0 mm": {
                    "M38x1.5": "M38x1.5"
                },
                "39.0 mm": {
                    "M39x4": "M39x4",
                    "M39x3": "M39x3",
                    "M39x2": "M39x2",
                    "M39x1.5": "M39x1.5",
                    "M39x1": "M39x1"
                },
                "40.0 mm": {
                    "M40x3": "M40x3",
                    "M40x2.5": "M40x2.5",
                    "M40x2": "M40x2",
                    "M40x1.5": "M40x1.5"
                },
                "42.0 mm": {
                    "M42x4.5": "M42x4.5",
                    "M42x4": "M42x4",
                    "M42x3": "M42x3",
                    "M42x2": "M42x2",
                    "M42x1.5": "M42x1.5",
                    "M42x1": "M42x1"
                },
                "45.0 mm": {
                    "M45x4.5": "M45x4.5",
                    "M45x4": "M45x4",
                    "M45x3": "M45x3",
                    "M45x2": "M45x2",
                    "M45x1.5": "M45x1.5",
                    "M45x1": "M45x1"
                },
                "48.0 mm": {
                    "M48x5": "M48x5",
                    "M48x4": "M48x4",
                    "M48x3": "M48x3",
                    "M48x2": "M48x2",
                    "M48x1.5": "M48x1.5"
                },
                "50.0 mm": {
                    "M50x4": "M50x4",
                    "M50x3": "M50x3",
                    "M50x2": "M50x2",
                    "M50x1.5": "M50x1.5"
                },
                "52.0 mm": {
                    "M52x5": "M52x5",
                    "M52x4": "M52x4",
                    "M52x3": "M52x3",
                    "M52x2": "M52x2",
                    "M52x1.5": "M52x1.5"
                },
                "55.0 mm": {
                    "M55x4": "M55x4",
                    "M55x3": "M55x3",
                    "M55x2": "M55x2",
                    "M55x1.5": "M55x1.5"
                },
                "56.0 mm": {
                    "M56x6.5": "M56x6.5",
                    "M56x5.5": "M56x5.5",
                    "M56x4": "M56x4",
                    "M56x3": "M56x3",
                    "M56x2": "M56x2",
                    "M56x1.5": "M56x1.5",
                    "M56x1": "M56x1"
                },
                "58.0 mm": {
                    "M58x4": "M58x4",
                    "M58x3": "M58x3",
                    "M58x2": "M58x2",
                    "M58x1.5": "M58x1.5"
                },
                "60.0 mm": {
                    "M60x5.5": "M60x5.5",
                    "M60x4": "M60x4",
                    "M60x3": "M60x3",
                    "M60x2": "M60x2",
                    "M60x1.5": "M60x1.5",
                    "M60x1": "M60x1"
                },
                "62.0 mm": {
                    "M62x4": "M62x4",
                    "M62x3": "M62x3",
                    "M62x2": "M62x2",
                    "M62x1.5": "M62x1.5"
                },
                "63.0 mm": {
                    "M63x1.5": "M63x1.5"
                },
                "64.0 mm": {
                    "M64x6": "M64x6",
                    "M64x5.5": "M64x5.5",
                    "M64x4": "M64x4",
                    "M64x3": "M64x3",
                    "M64x2": "M64x2",
                    "M64x1.5": "M64x1.5",
                    "M64x1": "M64x1"
                },
                "65.0 mm": {
                    "M65x4": "M65x4",
                    "M65x3": "M65x3",
                    "M65x2": "M65x2",
                    "M65x1.5": "M65x1.5"
                },
                "68.0 mm": {
                    "M68x6": "M68x6",
                    "M68x4": "M68x4",
                    "M68x3": "M68x3",
                    "M68x2": "M68x2",
                    "M68x1.5": "M68x1.5",
                    "M68x1": "M68x1"
                },
                "70.0 mm": {
                    "M70x6": "M70x6",
                    "M70x4": "M70x4",
                    "M70x3": "M70x3",
                    "M70x2": "M70x2",
                    "M70x1.5": "M70x1.5"
                },
                "72.0 mm": {
                    "M72x6": "M72x6",
                    "M72x4": "M72x4",
                    "M72x3": "M72x3",
                    "M72x2": "M72x2",
                    "M72x1.5": "M72x1.5",
                    "M72x1": "M72x1"
                },
                "75.0 mm": {
                    "M75x6": "M75x6",
                    "M75x4": "M75x4",
                    "M75x3": "M75x3",
                    "M75x2": "M75x2",
                    "M75x1.5": "M75x1.5"
                },
                "76.0 mm": {
                    "M76x6": "M76x6",
                    "M76x4": "M76x4",
                    "M76x3": "M76x3",
                    "M76x2": "M76x2",
                    "M76x1.5": "M76x1.5",
                    "M76x1": "M76x1"
                },
                "78.0 mm": {
                    "M78x2": "M78x2"
                },
                "80.0 mm": {
                    "M80x6": "M80x6",
                    "M80x4": "M80x4",
                    "M80x3": "M80x3",
                    "M80x2": "M80x2",
                    "M80x1.5": "M80x1.5",
                    "M80x1": "M80x1"
                },
                "82.0 mm": {
                    "M82x2": "M82x2"
                },
                "85.0 mm": {
                    "M85x6": "M85x6",
                    "M85x4": "M85x4",
                    "M85x3": "M85x3",
                    "M85x2": "M85x2",
                    "M85x1.5": "M85x1.5"
                },
                "90.0 mm": {
                    "M90x6": "M90x6",
                    "M90x4": "M90x4",
                    "M90x3": "M90x3",
                    "M90x2": "M90x2",
                    "M90x1.5": "M90x1.5"
                },
                "95.0 mm": {
                    "M95x6": "M95x6",
                    "M95x4": "M95x4",
                    "M95x3": "M95x3",
                    "M95x2": "M95x2",
                    "M95x1.5": "M95x1.5"
                },
                "100.0 mm": {
                    "M100x6": "M100x6",
                    "M100x4": "M100x4",
                    "M100x3": "M100x3",
                    "M100x2": "M100x2",
                    "M100x1.5": "M100x1.5"
                },
                "105.0 mm": {
                    "M105x6": "M105x6",
                    "M105x4": "M105x4",
                    "M105x3": "M105x3",
                    "M105x2": "M105x2",
                    "M105x1.5": "M105x1.5"
                },
                "110.0 mm": {
                    "M110x6": "M110x6",
                    "M110x4": "M110x4",
                    "M110x3": "M110x3",
                    "M110x2": "M110x2",
                    "M110x1.5": "M110x1.5"
                },
                "115.0 mm": {
                    "M115x6": "M115x6",
                    "M115x4": "M115x4",
                    "M115x3": "M115x3",
                    "M115x2": "M115x2",
                    "M115x1.5": "M115x1.5"
                },
                "120.0 mm": {
                    "M120x6": "M120x6",
                    "M120x4": "M120x4",
                    "M120x3": "M120x3",
                    "M120x2": "M120x2",
                    "M120x1.5": "M120x1.5"
                },
                "125.0 mm": {
                    "M125x8": "M125x8",
                    "M125x6": "M125x6",
                    "M125x4": "M125x4",
                    "M125x3": "M125x3",
                    "M125x2": "M125x2",
                    "M125x1.5": "M125x1.5"
                },
                "130.0 mm": {
                    "M130x8": "M130x8",
                    "M130x6": "M130x6",
                    "M130x4": "M130x4",
                    "M130x3": "M130x3",
                    "M130x2": "M130x2",
                    "M130x1.5": "M130x1.5"
                },
                "135.0 mm": {
                    "M135x6": "M135x6",
                    "M135x4": "M135x4",
                    "M135x3": "M135x3",
                    "M135x2": "M135x2",
                    "M135x1.5": "M135x1.5"
                },
                "140.0 mm": {
                    "M140x8": "M140x8",
                    "M140x6": "M140x6",
                    "M140x4": "M140x4",
                    "M140x3": "M140x3",
                    "M140x2": "M140x2",
                    "M140x1.5": "M140x1.5"
                },
                "145.0 mm": {
                    "M145x6": "M145x6",
                    "M145x4": "M145x4",
                    "M145x3": "M145x3",
                    "M145x2": "M145x2",
                    "M145x1.5": "M145x1.5"
                },
                "150.0 mm": {
                    "M150x8": "M150x8",
                    "M150x6": "M150x6",
                    "M150x4": "M150x4",
                    "M150x3": "M150x3",
                    "M150x2": "M150x2",
                    "M150x1.5": "M150x1.5"
                },
                "155.0 mm": {
                    "M155x6": "M155x6",
                    "M155x4": "M155x4",
                    "M155x3": "M155x3",
                    "M155x2": "M155x2"
                },
                "160.0 mm": {
                    "M160x8": "M160x8",
                    "M160x6": "M160x6",
                    "M160x4": "M160x4",
                    "M160x3": "M160x3",
                    "M160x2": "M160x2"
                },
                "165.0 mm": {
                    "M165x6": "M165x6",
                    "M165x4": "M165x4",
                    "M165x3": "M165x3",
                    "M165x2": "M165x2"
                },
                "170.0 mm": {
                    "M170x8": "M170x8",
                    "M170x6": "M170x6",
                    "M170x4": "M170x4",
                    "M170x3": "M170x3",
                    "M170x2": "M170x2"
                },
                "175.0 mm": {
                    "M175x6": "M175x6",
                    "M175x4": "M175x4",
                    "M175x3": "M175x3",
                    "M175x2": "M175x2"
                },
                "180.0 mm": {
                    "M180x8": "M180x8",
                    "M180x6": "M180x6",
                    "M180x4": "M180x4",
                    "M180x3": "M180x3",
                    "M180x2": "M180x2"
                },
                "185.0 mm": {
                    "M185x6": "M185x6",
                    "M185x4": "M185x4",
                    "M185x3": "M185x3",
                    "M185x2": "M185x2"
                },
                "190.0 mm": {
                    "M190x8": "M190x8",
                    "M190x6": "M190x6",
                    "M190x4": "M190x4",
                    "M190x3": "M190x3",
                    "M190x2": "M190x2"
                },
                "195.0 mm": {
                    "M195x6": "M195x6",
                    "M195x4": "M195x4",
                    "M195x3": "M195x3",
                    "M195x2": "M195x2"
                },
                "200.0 mm": {
                    "M200x8": "M200x8",
                    "M200x6": "M200x6",
                    "M200x4": "M200x4",
                    "M200x3": "M200x3",
                    "M200x2": "M200x2"
                },
                "205.0 mm": {
                    "M205x6": "M205x6",
                    "M205x4": "M205x4",
                    "M205x3": "M205x3",
                    "M205x2": "M205x2"
                },
                "210.0 mm": {
                    "M210x8": "M210x8",
                    "M210x6": "M210x6",
                    "M210x4": "M210x4",
                    "M210x3": "M210x3",
                    "M210x2": "M210x2"
                },
                "215.0 mm": {
                    "M215x6": "M215x6",
                    "M215x4": "M215x4",
                    "M215x3": "M215x3"
                },
                "220.0 mm": {
                    "M220x8": "M220x8",
                    "M220x6": "M220x6",
                    "M220x4": "M220x4",
                    "M220x3": "M220x3",
                    "M220x2": "M220x2"
                },
                "225.0 mm": {
                    "M225x6": "M225x6",
                    "M225x4": "M225x4",
                    "M225x3": "M225x3",
                    "M225x2": "M225x2"
                },
                "230.0 mm": {
                    "M230x6": "M230x6",
                    "M230x4": "M230x4",
                    "M230x3": "M230x3",
                    "M230x2": "M230x2"
                },
                "235.0 mm": {
                    "M235x6": "M235x6",
                    "M235x4": "M235x4",
                    "M235x3": "M235x3"
                },
                "240.0 mm": {
                    "M240x8": "M240x8",
                    "M240x6": "M240x6",
                    "M240x4": "M240x4",
                    "M240x3": "M240x3",
                    "M240x2": "M240x2"
                },
                "245.0 mm": {
                    "M245x6": "M245x6",
                    "M245x4": "M245x4",
                    "M245x3": "M245x3",
                    "M245x2": "M245x2"
                },
                "250.0 mm": {
                    "M250x8": "M250x8",
                    "M250x6": "M250x6",
                    "M250x4": "M250x4",
                    "M250x3": "M250x3",
                    "M250x2": "M250x2"
                },
                "255.0 mm": {
                    "M255x6": "M255x6",
                    "M255x4": "M255x4",
                    "M255x3": "M255x3"
                },
                "260.0 mm": {
                    "M260x8": "M260x8",
                    "M260x6": "M260x6",
                    "M260x4": "M260x4",
                    "M260x3": "M260x3"
                },
                "265.0 mm": {
                    "M265x6": "M265x6",
                    "M265x4": "M265x4",
                    "M265x3": "M265x3"
                },
                "270.0 mm": {
                    "M270x6": "M270x6",
                    "M270x4": "M270x4",
                    "M270x3": "M270x3"
                },
                "275.0 mm": {
                    "M275x6": "M275x6",
                    "M275x4": "M275x4",
                    "M275x3": "M275x3"
                },
                "280.0 mm": {
                    "M280x8": "M280x8",
                    "M280x6": "M280x6",
                    "M280x4": "M280x4",
                    "M280x3": "M280x3"
                },
                "285.0 mm": {
                    "M285x6": "M285x6",
                    "M285x4": "M285x4",
                    "M285x3": "M285x3"
                },
                "290.0 mm": {
                    "M290x6": "M290x6",
                    "M290x4": "M290x4",
                    "M290x3": "M290x3"
                },
                "295.0 mm": {
                    "M295x6": "M295x6",
                    "M295x4": "M295x4",
                    "M295x3": "M295x3"
                },
                "300.0 mm": {
                    "M300x8": "M300x8",
                    "M300x6": "M300x6",
                    "M300x4": "M300x4",
                    "M300x3": "M300x3"
                },
                "310.0 mm": {
                    "M310x6": "M310x6",
                    "M310x4": "M310x4"
                },
                "320.0 mm": {
                    "M320x6": "M320x6",
                    "M320x4": "M320x4"
                },
                "330.0 mm": {
                    "M330x6": "M330x6",
                    "M330x4": "M330x4"
                },
                "340.0 mm": {
                    "M340x6": "M340x6",
                    "M340x4": "M340x4"
                },
                "350.0 mm": {
                    "M350x6": "M350x6",
                    "M350x4": "M350x4"
                },
                "360.0 mm": {
                    "M360x6": "M360x6",
                    "M360x4": "M360x4"
                },
                "370.0 mm": {
                    "M370x6": "M370x6",
                    "M370x4": "M370x4"
                },
                "380.0 mm": {
                    "M380x6": "M380x6",
                    "M380x4": "M380x4"
                },
                "390.0 mm": {
                    "M390x6": "M390x6",
                    "M390x4": "M390x4"
                },
                "400.0 mm": {
                    "M400x6": "M400x6",
                    "M400x4": "M400x4"
                },
                "410.0 mm": {
                    "M410x6": "M410x6"
                },
                "420.0 mm": {
                    "M420x6": "M420x6"
                },
                "430.0 mm": {
                    "M430x6": "M430x6"
                },
                "440.0 mm": {
                    "M440x6": "M440x6"
                },
                "450.0 mm": {
                    "M450x6": "M450x6"
                },
                "460.0 mm": {
                    "M460x6": "M460x6"
                },
                "470.0 mm": {
                    "M470x6": "M470x6"
                },
                "480.0 mm": {
                    "M480x6": "M480x6"
                },
                "490.0 mm": {
                    "M490x6": "M490x6"
                },
                "500.0 mm": {
                    "M500x6": "M500x6"
                },
                "510.0 mm": {
                    "M510x6": "M510x6"
                },
                "520.0 mm": {
                    "M520x6": "M520x6"
                },
                "530.0 mm": {
                    "M530x6": "M530x6"
                },
                "540.0 mm": {
                    "M540x6": "M540x6"
                },
                "550.0 mm": {
                    "M550x6": "M550x6"
                },
                "560.0 mm": {
                    "M560x6": "M560x6"
                },
                "570.0 mm": {
                    "M570x6": "M570x6"
                },
                "580.0 mm": {
                    "M580x6": "M580x6"
                },
                "590.0 mm": {
                    "M590x6": "M590x6"
                },
                "600.0 mm": {
                    "M600x6": "M600x6"
                },
                "1060.0 mm": {
                    "M1060x8": "M1060x8"
                }
            }
        }
    }
}
